import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { NAV, ENQUIRE } from "@/lib/site";
import { COLLECTION_MEGA } from "@/lib/timber";

const tid = (to) => `nav-child-${to.split("/").pop()}`;

const renderLabel = (label) =>
  label.endsWith(" Collection") ? (
    <>
      <span className="block">{label.slice(0, -" Collection".length)}</span>
      <span className="block">Collection</span>
    </>
  ) : (
    label
  );

const Column = ({ col }) => (
  <div className="whitespace-nowrap">
    <Link to={col.to} data-testid={tid(col.to)} className="eyebrow block hover:text-foreground">
      {renderLabel(col.label)}
    </Link>
    {col.groups && (
      <div className="mt-4 space-y-2.5">
        {col.groups.map((g) => (
          <Link
            key={g.to}
            to={g.to}
            data-testid={tid(g.to)}
            className="block text-sm tracking-editorial text-muted-foreground transition-colors hover:text-foreground"
          >
            {g.label}
          </Link>
        ))}
      </div>
    )}
  </div>
);

const DesktopMega = ({ open, onEnter, onLeave }) => (
  <div
    onMouseEnter={onEnter}
    onMouseLeave={onLeave}
    className={`fixed right-4 top-[5.5rem] z-40 transition-opacity duration-300 ${
      open ? "visible opacity-100" : "invisible pointer-events-none opacity-0"
    }`}
  >
    <div className="flex max-w-[calc(100vw-2rem)] flex-wrap items-start gap-x-9 gap-y-6 border hairline bg-white px-7 py-6" data-testid="collection-mega">
      {COLLECTION_MEGA.columns.map((col) => (
        <Column key={col.to} col={col} />
      ))}
    </div>
  </div>
);

const MobileCollection = () => (
  <div className="pb-4" data-testid="mobile-collection-tree">
    {COLLECTION_MEGA.columns.map((col) => (
      <div key={col.to} className="mb-4">
        <Link to={col.to} data-testid={`mobile-${tid(col.to)}`} className="block py-2 text-sm uppercase tracking-[0.16em]">
          {col.label}
        </Link>
        {col.groups && (
          <div className="ml-3 border-l border-border/60 pl-3">
            {col.groups.map((g) => (
              <div key={g.to}>
                <Link to={g.to} data-testid={`mobile-${tid(g.to)}`} className="block py-2 text-[0.72rem] uppercase tracking-[0.16em] text-muted-foreground">
                  {g.label}
                </Link>
                {g.items && (
                  <div className="ml-3 border-l border-border/60 pl-3">
                    {g.items.map((it) => (
                      <Link key={it.to} to={it.to} data-testid={`mobile-${tid(it.to)}`} className="block py-1.5 text-[0.68rem] uppercase tracking-[0.14em] text-muted-foreground">
                        {it.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    ))}
  </div>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const [megaOpen, setMegaOpen] = useState(false);
  const megaTimer = useRef(null);
  const { pathname } = useLocation();

  const openMega = () => {
    if (megaTimer.current) clearTimeout(megaTimer.current);
    setMegaOpen(true);
  };
  const closeMega = () => {
    if (megaTimer.current) clearTimeout(megaTimer.current);
    megaTimer.current = setTimeout(() => setMegaOpen(false), 150);
  };

  useEffect(() => {
    setOpen(false);
    setOpenGroup(null);
    setMegaOpen(false);
  }, [pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed inset-x-0 top-0 z-50 border-b hairline bg-white"
      >
        <div className="edge flex h-20 items-center justify-between md:h-24">
          <Logo size="h-11 md:h-14" />

          <nav className="hidden items-center gap-8 lg:flex">
            {NAV.map((item) =>
              item.children ? (
                <div
                  key={item.id}
                  className="relative"
                  onMouseEnter={openMega}
                  onMouseLeave={closeMega}
                >
                  <NavLink
                    to={item.to}
                    data-testid={`nav-${item.id}`}
                    className={({ isActive }) =>
                      `text-[0.72rem] uppercase tracking-[0.18em] transition-colors duration-300 hover:text-foreground ${
                        isActive ? "text-foreground" : "text-muted-foreground"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                  <DesktopMega open={megaOpen} onEnter={openMega} onLeave={closeMega} />
                </div>
              ) : (
                <NavLink
                  key={item.id}
                  to={item.to}
                  data-testid={`nav-${item.id}`}
                  className={({ isActive }) =>
                    `text-[0.72rem] uppercase tracking-[0.18em] transition-colors duration-300 hover:text-foreground ${
                      isActive ? "text-foreground" : "text-muted-foreground"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
            <Link
              to={ENQUIRE.to}
              data-testid="nav-enquire"
              className="border border-foreground px-5 py-2.5 text-[0.72rem] uppercase tracking-[0.18em] transition-colors duration-300 hover:bg-foreground hover:text-background"
            >
              {ENQUIRE.label}
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 flex-col items-center justify-center gap-[6px] lg:hidden"
          >
            <span className={`h-px w-6 bg-foreground transition-transform duration-300 ${open ? "translate-y-[3.5px] rotate-45" : ""}`} />
            <span className={`h-px w-6 bg-foreground transition-transform duration-300 ${open ? "-translate-y-[3.5px] -rotate-45" : ""}`} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 overflow-y-auto bg-white pt-20 lg:hidden"
          >
            <nav className="edge flex flex-col hairline">
              {NAV.map((item) => (
                <div key={item.id} className="border-b hairline">
                  {item.children ? (
                    <>
                      <button
                        onClick={() => setOpenGroup((g) => (g === item.id ? null : item.id))}
                        data-testid={`mobile-nav-${item.id}`}
                        className="flex w-full items-center justify-between py-5 text-left text-2xl font-light tracking-editorial"
                      >
                        {item.label}
                        <span className="text-base">{openGroup === item.id ? "–" : "+"}</span>
                      </button>
                      {openGroup === item.id && <MobileCollection />}
                    </>
                  ) : (
                    <Link
                      to={item.to}
                      data-testid={`mobile-nav-${item.id}`}
                      className="block py-5 text-2xl font-light tracking-editorial"
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
              <Link
                to={ENQUIRE.to}
                data-testid="mobile-nav-enquire"
                className="mt-8 border border-foreground py-4 text-center text-sm uppercase tracking-[0.18em]"
              >
                {ENQUIRE.label}
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
